import { useEffect, useState } from "react";
import useTasks from "../../../hooks/useTasks";
import useDisplayNotifications from "../../../hooks/useDisplayNotifications";
import { ETaskStatus, ITask } from "../../../data_models/task";
import { IFreelancer } from "../../../data_models/freelancer";
import Lightbox from "../../common/Lightbox";
import LoadingIndicator from "../../common/LoadingIndicator";
import { DisplayNotificationType } from "../../../models/DisplayNotification";
import useProjects from "../../../hooks/useProjects";
import { EProjectStatus, IProject } from "../../../data_models/project";
import { Chip } from "../../common/Chip";
import { FilledButton, OutlinedButton } from "../../common/Button";
import { getFreelancerName } from "../../../utils/freelancerUtils";
import Accordion from "../../common/Accordion";
import useConfig from "../../../hooks/useConfig";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { EUserType } from "../../../data_models/enums";

interface ShortlistFreelancerLightboxProps {
    onClose: () => void;
    freelancer: IFreelancer;
}

const ShortlistFreelancerLightbox: React.FC<ShortlistFreelancerLightboxProps> = ({ onClose, freelancer }) => {
    const [loadingFetchTasks, setLoadingFetchTasks] = useState(false);
    const [loadingTaskId, setLoadingTaskId] = useState<string | null>(null);
    const { currentUser, currentUserType } = useCurrentUser();

    const [tasks, setTasks] = useState<ITask[]>([]);
    const [projects, setProjects] = useState<IProject[]>([]);
    const [projectTasksMap, setProjectTasksMap] = useState<Map<string, ITask[]>>(new Map());

    const { fetchUnassignedTasksForPm, fetchTasksByFilter, shortlistFreelancerToggle } = useTasks();
    const { fetchMyProjectsAsPm, fetchProjectsByFilter } = useProjects();
    const { addNotification } = useDisplayNotifications();
    const { config } = useConfig();

    useEffect(() => {
        setLoadingFetchTasks(true);
        let fetchProjectsDone = false;
        let fetchTasksDone = false;

        // fetch tasks for freelancer (pm)
        if (currentUserType === EUserType.FREELANCER) {
            fetchMyProjectsAsPm(1, 99999).then(projects => {
                // filter ongoing projects
                const ongoingProjects = projects.filter(project => project.status &&
                    project.status >= EProjectStatus.WAITING_FOR_PM &&
                    project.status <= EProjectStatus.COMPLETED);

                setProjects(ongoingProjects);

                const taskFilter = {
                    projectId: {
                        $in: ongoingProjects.map(p => p._id)
                    },
                    currentStatus: {
                        $lt: ETaskStatus.COMPLETED
                    }
                }
    
                fetchTasksByFilter(taskFilter, 1, 99999).then(tasks => {
                    setTasks(tasks);
    
                    // Create project tasks map
                    const projectTasksMap = new Map<string, ITask[]>();
                    tasks.forEach((task: ITask) => {
                        if (task.projectId) {
                            if (!projectTasksMap.has(task.projectId)) {
                                projectTasksMap.set(task.projectId, []);
                            }
                            projectTasksMap.get(task.projectId)!.push(task);
                        }
                    });
                    setProjectTasksMap(projectTasksMap);
                }).finally(() => {
                    fetchTasksDone = true;
                    if (fetchProjectsDone && fetchTasksDone) {
                        setLoadingFetchTasks(false);
                    }
                });
    
            }).finally(() => {
                fetchProjectsDone = true;
                if (fetchProjectsDone && fetchTasksDone) {
                    setLoadingFetchTasks(false);
                }
            });
            
        } else if (currentUserType === EUserType.EMPLOYER) {
            // fetch tasks for employer
            const projectFilter = {
                employerId: currentUser?._id,
                status: {
                    $lt: EProjectStatus.COMPLETED
                }
            }

            fetchProjectsByFilter(projectFilter, 1, 99999).then(projects => {
                console.log(projects.map(p => p.project));
                setProjects(projects.map(p => p.project));
                fetchProjectsDone = true;

                const taskFilter = {
                    projectId: {
                        $in: projects.map(p => p.project._id)
                    },
                    currentStatus: {
                        $lt: ETaskStatus.COMPLETED
                    }
                }
                fetchTasksByFilter(taskFilter, 1, 99999).then(tasks => {
                    setTasks(tasks);

                    // Create project tasks map
                    const projectTasksMap = new Map<string, ITask[]>();
                    tasks.forEach((task: ITask) => {
                        if (task.projectId) {
                            if (!projectTasksMap.has(task.projectId)) {
                                projectTasksMap.set(task.projectId, []);
                            }
                            projectTasksMap.get(task.projectId)!.push(task);
                        }
                    });
                    setProjectTasksMap(projectTasksMap);
                }).finally(() => {
                    fetchTasksDone = true;
                    if (fetchProjectsDone && fetchTasksDone) {
                        setLoadingFetchTasks(false);
                    }
                });
            });
        }
    }, [])

    const isFreelancerInShortlist = (task: ITask) => {
        if (currentUserType === EUserType.FREELANCER) {
            return task.freelancerShortlistByPm?.includes(freelancer._id!);
        } else {
            return task.freelancerShortlistByEmployer?.includes(freelancer._id!);
        }
    }

    const onShortlistFreelancer = async (taskId: string) => {
        if (taskId) {
            setLoadingTaskId(taskId);
            try {
                const updatedTask = await shortlistFreelancerToggle(taskId, freelancer._id!);
                if (updatedTask) {
                    // Update the task in the tasks array
                    setTasks(prevTasks =>
                        prevTasks.map(task =>
                            task._id === updatedTask._id ? updatedTask : task
                        )
                    );
                    // Update the task in the projectTasksMap
                    setProjectTasksMap(prevMap => {
                        const newMap = new Map(prevMap);
                        Array.from(newMap.entries()).forEach(([projectId, tasks]) => {
                            const updatedTasks = tasks.map(task =>
                                task._id === updatedTask._id ? updatedTask : task
                            );
                            newMap.set(projectId, updatedTasks);
                        });
                        return newMap;
                    });
                }
            } finally {
                setLoadingTaskId(null);
            }
        }
    }

    return (
        <Lightbox
            content={
                <div className="" >
                    <h3>Select Tasks to Shortlist {getFreelancerName(freelancer)}</h3>

                    <div>
                        {loadingFetchTasks && projectTasksMap
                            ? <div className=""><LoadingIndicator /></div>
                            : <div>
                                {projects.map(project => (
                                    <div key={project._id} className="my-4">
                                        <Accordion
                                            title={
                                                <div>
                                                    <p className="font-bold">{project.title}</p>
                                                    <p className={`caption2 italic ${(projectTasksMap.get(project._id!)?.length || 0) === 0
                                                        ? ''
                                                        : 'font-bold'}`}>{projectTasksMap.get(project._id!)?.length || 0} available tasks</p>
                                                </div>
                                            }
                                            content={
                                                <div className="flex flex-col space-y-2">
                                                    {projectTasksMap.get(project._id!)
                                                        ? projectTasksMap.get(project._id!)?.map(task => (
                                                            <div
                                                                key={task._id}
                                                                className="px-4 py-2 rounded-md border border-neutral-200"
                                                            >
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex-1">
                                                                        <p className="font-bold">{task.title}</p>
                                                                        <p className="caption2">{`Credits: ${task.estimatedCredits} (${(task.estimatedCredits || 0) * (config?.rateCreditsToUsd.freelancer || 0)} USD)`}</p>
                                                                        <div className="my-2 flex flex-wrap gap-2">
                                                                            {task.requiredSkills?.map(skill => (
                                                                                <div key={skill}>
                                                                                    <Chip>{skill}</Chip>
                                                                                </div>
                                                                            ))}
                                                                            {task.requiredTools?.map(tool => (
                                                                                <div key={tool}>
                                                                                    <Chip theme="secondary">{tool}</Chip>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        {isFreelancerInShortlist(task) ? (
                                                                            <OutlinedButton
                                                                                onClick={() => onShortlistFreelancer(task._id!)}
                                                                                isLoading={loadingTaskId === task._id}
                                                                            >
                                                                                Remove from Shortlist
                                                                            </OutlinedButton>
                                                                        ) : (
                                                                            <FilledButton
                                                                                onClick={() => onShortlistFreelancer(task._id!)}
                                                                                isLoading={loadingTaskId === task._id}
                                                                            >
                                                                                Shortlist {getFreelancerName(freelancer).split(' ')[0]}
                                                                            </FilledButton>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                        : <p className="caption2">This project has currently no available tasks</p>}
                                                </div>
                                            }
                                            theme="primary"
                                            highlightWhenOpen={true}
                                        />
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                </div>}
            onClose={onClose}
        />
    );
}

export default ShortlistFreelancerLightbox;